import React, { useEffect } from 'react';

import { css } from '@emotion/react';
import {
  init as initFullStory,
  isInitialized as isFullStoryInitialized,
} from '@fullstory/browser';
import { colors, HStack, Primitive, VStack } from '@rtkwlf/fenrir-react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { Outlet, useMatch } from 'react-router-dom';

import { ErrorBoundarySadWolf } from '@platform/client/components/error-boundaries/error-boundary-sad-wolf';

import { DashboardAppBar } from '@packages/core/layout/dashboard-app-bar/dashboard-app-bar';
import { DashboardLeftMenu } from '@packages/core/layout/dashboard-left-menu/dashboard-left-menu';
import { DashboardNavigation } from '@packages/core/layout/dashboard-navigation/dashboard-navigation';
import { FeatureFlagsDialog } from '@packages/platform/feature-flags';
import { FLAG_MANAGER } from 'ae-constants/constants';
import {
  INSIGHTS_ROUTE,
  ORGANIZATION_SWITCH_ROUTE,
} from 'ae-constants/routes.constants';
import { ConfirmationProvider } from 'ae-kernel/confirmation-context/confirmation-context';

import { useFeatureReleases } from './use-feature-releases/use-feature-releases';

export const Main: React.FC = () => {
  const featureReleases = useFeatureReleases();

  useEffect(() => {
    if (window.location.host === 'd38e7hqqh4z3sq.cloudfront.net') {
      const newURL = new URL(window.location.href);
      newURL.host = 'dashboard.arcticwolf.com';
      Sentry.captureMessage('Redirect from CDN URL');
      window.location.replace(newURL);
    }
  }, []);

  const { t } = useTranslation();

  if (import.meta.env.MODE === 'production' && !isFullStoryInitialized()) {
    initFullStory({ orgId: 'o-1JY8D8-na1' });
  }

  const isOrgSwitchView = useMatch(ORGANIZATION_SWITCH_ROUTE);
  const insightsRouteMatch = useMatch({
    end: false,
    path: INSIGHTS_ROUTE,
  });

  // Will be able to remove this once Main is extracted to components and added on a per route basis.
  if (isOrgSwitchView) {
    return <Outlet />;
  }

  return (
    <ConfirmationProvider
      defaultParams={{
        cancelText: t('confirmation:cancel'),
        confirmText: t('confirmation:ok'),
        description: t('confirmation:areYouSure'),
        title: t('confirmation:confirmation'),
      }}
    >
      <VStack
        gap="zero"
        height="full"
        width="full"
        justifyContent="flex-start"
        alignItems="flex-start"
        overflow="hidden"
        css={css({
          '@media print': {
            display: 'block',
          },
        })}
      >
        <Primitive.div
          zIndex={3}
          width="full"
          height="auto"
          css={
            insightsRouteMatch &&
            css({
              '@media print': {
                display: 'none',
              },
            })
          }
        >
          <DashboardAppBar featureReleases={featureReleases} />
          <DashboardNavigation />
        </Primitive.div>
        <ErrorBoundarySadWolf>
          <HStack
            alignItems="flex-start"
            width="full"
            height="full"
            overflow="hidden"
            gap="zero"
          >
            <DashboardLeftMenu />
            <Primitive.div
              height="full"
              width="full"
              overflow="auto"
              padding={{
                laptop: 'xxlarge',
                mobile: 'small',
                tablet: 'medium',
                ultrawide: 'xxlarge',
              }}
              paddingY="zero"
              css={css({
                '@media print': {
                  backgroundColor: `rgb(${colors.background.widget.default})`,
                },

                // TODO: Remove this once all layouts have been converted to fenrir v2 templates
                '&:has(.layout-v2)': {
                  minHeight: 0,
                  padding: 0,
                },

                // TODO: Remove this once all layouts have been converted to fenrir v2 templates
                "html:is(.light, [data-mui-color-scheme='light'], [data-theme-mode='light']) &:has(.layout-v2)":
                  {
                    background: 'var(--aw-palette-arcticGrey-A100)',
                  },
                // TODO: Remove this once all layouts have been converted to fenrir v2 templates
                "html:is(.dark, [data-mui-color-scheme='dark'], [data-theme-mode='dark']) &:has(.layout-v2)":
                  {
                    background: 'var(--aw-palette-arcticGrey-A200)',
                  },
              })}
              data-testid="main-content"
            >
              <Primitive.div
                paddingY="medium"
                css={css({
                  '@media print': {
                    padding: 0,
                  },

                  // TODO: Remove this once all layouts have been converted to fenrir v2 templates
                  '&:has(.layout-v2)': {
                    height: '100%',
                    padding: 0,
                  },
                })}
              >
                <React.Suspense>
                  <Outlet />
                </React.Suspense>
              </Primitive.div>
            </Primitive.div>
          </HStack>
          {(import.meta.env.MODE === 'development' ||
            window.localStorage.getItem(FLAG_MANAGER) === 'true') && (
            <FeatureFlagsDialog />
          )}
        </ErrorBoundarySadWolf>
      </VStack>
    </ConfirmationProvider>
  );
};
